import React from "react";
import { Coins, Package, Users, DollarSign } from "lucide-react";
import { t } from "@/lib/i18n";

const STREAM_META = {
  referral: {
    icon: Users,
    tint: "text-[#60a5fa]",
    border: "border-[#60a5fa]/30",
    bg: "bg-[#60a5fa]/10",
    key: "stream_referral",
    subKey: "stream_referral_sub",
  },
  amazon: {
    icon: Package,
    tint: "text-[#f3c669]",
    border: "border-[#f3c669]/40",
    bg: "bg-[#f3c669]/10",
    key: "stream_amazon",
    subKey: "stream_amazon_sub",
  },
  marketplace: {
    icon: Coins,
    tint: "text-[#c084fc]",
    border: "border-[#c084fc]/30",
    bg: "bg-[#c084fc]/10",
    key: "stream_marketplace",
    subKey: "stream_marketplace_sub",
  },
};

const fmtUSD = (n) =>
  `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const fmtVND = (n) =>
  new Intl.NumberFormat("vi-VN", { maximumFractionDigits: 0 }).format(n || 0);

export default function EarningsStreams({ lang, streams, combinedUsd, combinedVnd }) {
  return (
    <section
      className="eigoo-card p-6 sm:p-7 anim-fade-up anim-delay-2"
      data-testid="earnings-streams-card"
    >
      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
        <div>
          <h3 className="font-display text-lg sm:text-xl font-bold">
            {t(lang, "streams_title")}
          </h3>
          <p className="text-xs text-slate-400 mt-0.5 max-w-md">
            {t(lang, "streams_sub")}
          </p>
        </div>
        <div className="rounded-xl border border-[#d4a843]/40 bg-[#0a1628]/70 px-4 py-2.5">
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#f3c669]">
            {t(lang, "combined_total")}
          </div>
          <div
            className="font-display text-2xl sm:text-3xl font-extrabold gold-gradient-text tabular-nums"
            data-testid="combined-earnings-total"
          >
            {fmtUSD(combinedUsd)}
          </div>
          {lang === "vi" && (
            <div className="text-[11px] text-slate-400 mt-0.5">
              {t(lang, "approx")} ₫{fmtVND(combinedVnd)}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {(streams || []).map((s, i) => {
          const meta = STREAM_META[s.stream] || STREAM_META.referral;
          const Icon = meta.icon;
          return (
            <div
              key={s.stream}
              data-testid={`stream-card-${s.stream}`}
              className={`rounded-xl border ${meta.border} ${meta.bg} p-4 anim-fade-up anim-delay-${(i % 4) + 1}`}
            >
              <div className="flex items-center justify-between">
                <div className={`w-9 h-9 rounded-lg grid place-items-center ${meta.tint} bg-black/25 border ${meta.border}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                  {s.entries} {t(lang, "entries")}
                </span>
              </div>
              <div className="mt-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-300">
                {t(lang, meta.key)}
              </div>
              <div className="font-display mt-1.5 text-2xl font-extrabold text-white tabular-nums">
                {fmtUSD(s.total_usd)}
              </div>
              <div className="text-[11px] text-slate-400 mt-1">{t(lang, meta.subKey)}</div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 flex items-center gap-2 text-[11px] text-slate-500">
        <DollarSign className="w-3.5 h-3.5" />
        {t(lang, "streams_future_hint")}
      </div>
    </section>
  );
}
